import { Button } from "components/DesignSystem/Button/Button";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { MobileInput } from "components/DesignSystem/MobileInput/MobileInput";
import { Switch } from "components/DesignSystem/Switch/Switch";
import OtpBox from "components/design/otpBox";
import { FormikForm } from "components/FormikForm/FormikForm";
import { Pencil20 } from "components/icons/Pencil20";
import { PhoneCallIcon } from "components/icons/PhoneCallIcon";
import { RightArrow } from "components/icons/RightArrow";
import { useToast } from "hooks/useToast";
import authContext from "jwt_context&axios/authContext";
import { ReactNode, useContext, useEffect, useState } from "react";
import {
  useResendMobileOtpMutation,
  useSendOtpForMobileMutation,
  useVerifyMobileOtpMutation,
} from "store/apis/settings";
import { Profile } from "types/Models/user";
import { getCurrentTimezone } from "utils/getCurrentTimezone";
import { readReferralCode } from "v2/utils/readReferralCode";
import * as Yup from "yup";
import { useOnboardingData } from "../../OnboardingDataContext";

type PhoneStepProps = {
  onComplete: (summary: ReactNode) => void;
};

type PhoneValues = { mobile: string; mobile_country?: string };

const RESEND_COUNTDOWN_SECONDS = 60;
const COUNTDOWN_TICK_MS = 1000;

const validationSchema = Yup.object({
  mobile: Yup.string().trim().required("Phone number is required"),
});

export const buildPhoneSummary = (mobile: string): ReactNode => (
  <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-60">
    <span className="t-flex t-shrink-0 t-text-purple-70 t-bg-surface-purple t-p-1 t-rounded">
      <PhoneCallIcon />
    </span>
    <span>{mobile}</span>
  </div>
);

export const PhoneStep = ({ onComplete }: PhoneStepProps) => {
  const { data, patchData } = useOnboardingData();
  const { setUser, setAuthtoken } = useContext(authContext);
  const { alertToast } = useToast();

  const [sendOtpForMobile, { isLoading: isSending }] =
    useSendOtpForMobileMutation();
  const [verifyMobileOtp, { isLoading: isVerifying }] =
    useVerifyMobileOtpMutation();
  const [resendMobileOtp] = useResendMobileOtpMutation();

  const [mobile, setMobile] = useState<string | null>(null);
  const [prefillMobile, setPrefillMobile] = useState("");
  const [mobileCountry, setMobileCountry] = useState<string | undefined>();
  const [prefillCountry, setPrefillCountry] = useState<string | undefined>();
  const [waConsent, setWaConsent] = useState(true);
  const [otp, setOtp] = useState("");
  const [inCorrectOtp, setInCorrectOtp] = useState(false);
  const [resendSeconds, setResendSeconds] = useState(0);

  useEffect(() => {
    if (resendSeconds <= 0) return;
    const timer = setTimeout(
      () => setResendSeconds((s) => s - 1),
      COUNTDOWN_TICK_MS
    );
    return () => clearTimeout(timer);
  }, [resendSeconds]);

  const sendOtp = async (values: PhoneValues) => {
    try {
      await sendOtpForMobile({
        mobile: values.mobile,
        email: data.email,
        whatsapp_consent: waConsent,
      }).unwrap();
      setMobile(values.mobile);
      setMobileCountry(values.mobile_country);
      setResendSeconds(RESEND_COUNTDOWN_SECONDS);
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const persistAuth = (profile: Profile) => {
    setAuthtoken(profile);
    setUser(profile.access);
    localStorage.setItem("authTokens", JSON.stringify(profile));
  };

  const verifyOtp = async (enteredOtp: string) => {
    if (!mobile) return;
    try {
      const profile = await verifyMobileOtp({
        payload: {
          mobile,
          profile_id: data.profileId,
          otp: enteredOtp,
          wa_consent: waConsent,
          referral_code: readReferralCode(),
          current_timezone: getCurrentTimezone(),
        },
      }).unwrap();

      persistAuth(profile);
      patchData({ mobile, profileId: profile.uuid || data.profileId });
      onComplete(buildPhoneSummary(mobile));
    } catch (error: any) {
      setInCorrectOtp(true);
      setOtp("");
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const handleResend = async () => {
    if (!mobile) return;
    setOtp("");
    setInCorrectOtp(false);
    try {
      await resendMobileOtp({ mobile, email: data.email }).unwrap();
      setResendSeconds(RESEND_COUNTDOWN_SECONDS);
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  const whatsappToggle = (
    <label
      htmlFor="whatsapp_consent"
      className="t-flex t-items-center t-gap-3 t-py-3 t-cursor-pointer"
    >
      <Switch
        name="whatsapp_consent"
        checked={waConsent}
        onCheckedChange={setWaConsent}
        className="sm:t-shrink-0"
      />
      <div>
        <div className="t-text-body t-text-text-60">
          Enable WhatsApp notifications
        </div>
        <div className="t-text-body-sm t-text-text-30">
          Allow important compliance updates &amp; requests via WhatsApp.
        </div>
      </div>
    </label>
  );

  const handleEditPhone = () => {
    if (mobile) setPrefillMobile(mobile);
    setPrefillCountry(mobileCountry);
    setMobile(null);
    setOtp("");
    setInCorrectOtp(false);
    setResendSeconds(0);
  };

  if (mobile) {
    return (
      <div className="t-flex t-flex-col t-gap-4">
        <div className="t-flex t-items-center t-gap-1 t-text-body-sm t-text-text-30">
          <span>Enter the 6-digit code sent to</span>
          <span className="t-text-text-60">{mobile}</span>
          <Button
            customType="ghost_icon"
            size="small"
            onClick={handleEditPhone}
            title="Edit phone number"
          >
            <Pencil20 className="t-size-4" />
          </Button>
        </div>
        <OtpBox
          otp={otp}
          setOtp={setOtp}
          getOtp={verifyOtp}
          inCorrectOtp={inCorrectOtp}
          width="100%"
          setInvalidOtp={() => {}}
        />
        {isVerifying && (
          <div className="t-flex t-items-center t-gap-2 t-text-body-sm t-text-text-30">
            <Spinner size="small" />
            <span>Verifying code...</span>
          </div>
        )}
        {!isVerifying && inCorrectOtp && (
          <div className="t-text-body-sm t-text-red">
            Incorrect code. Try again.
          </div>
        )}
        {resendSeconds > 0 ? (
          <div className="t-text-body-sm t-text-text-30 t-italic">
            Didn&apos;t receive it? Resend in {resendSeconds} seconds
          </div>
        ) : (
          <div className="t-text-body-sm t-text-text-30 t-font-light t-items-center t-flex t-gap-1 t-italic">
            <span>Didn&apos;t receive it? </span>
            <Button customType="link" onClick={handleResend}>
              <span className="t-italic">Resend</span>
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <FormikForm<PhoneValues>
      initialValues={{ mobile: prefillMobile, mobile_country: prefillCountry }}
      validationSchema={validationSchema}
      validateOnChange={false}
      onSubmit={sendOtp}
    >
      <div className="t-flex t-flex-col t-gap-4">
        <MobileInput name="mobile" label="Phone number" />
        {whatsappToggle}
        <div>
          <Button
            type="submit"
            customType="primary"
            isLoading={isSending}
            rightIcon={<RightArrow />}
          >
            Send code
          </Button>
        </div>
      </div>
    </FormikForm>
  );
};
